import { ClipboardList } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourseSignups, getCourses } from '../../firebase/firestore';
import { useTenant } from '../../hooks/useTenant';
import { StudentStatusPageShell } from '../../components/dashboard/StudentStatusPageShell';
import { formatDate } from '../../utils/date';

function formatStudentName(item) {
  return item?.fullName || `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || '-';
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getGraduationDate(item) {
  return toDate(item?.graduatedAt)
    || toDate(item?.graduationDate)
    || toDate(item?.graduatedOn)
    || toDate(item?.updatedAt)
    || toDate(item?.createdAt)
    || null;
}

export function GraduatedStudentsPage() {
  const { activeTenantId } = useTenant();
  const [loading, setLoading] = useState(true);
  const [signups, setSignups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const load = useCallback(async () => {
    if (!activeTenantId) {
      setSignups([]);
      setCourses([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [nextSignups, nextCourses] = await Promise.all([
        getCourseSignups(activeTenantId),
        getCourses(activeTenantId),
      ]);
      setSignups(nextSignups);
      setCourses(nextCourses);
    } catch (error) {
      console.error(error);
      setSignups([]);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  const courseLookup = useMemo(
    () => new Map(courses.map((course) => [course.id, course])),
    [courses],
  );

  const rows = useMemo(() => {
    const search = normalizeText(searchTerm);

    return signups
      .filter((item) => normalizeText(item.status) === 'graduated')
      .map((item) => ({
        ...item,
        courseName: item.courseName || courseLookup.get(item.courseId)?.name || '-',
        graduationDate: getGraduationDate(item),
        graduationNote: item.graduatedNote || item.graduationNote || '',
      }))
      .filter((item) => {
        if (!search) return true;
        return [
          formatStudentName(item),
          item.courseName,
          item.intakeLabel,
          item.graduationNote,
          item.email,
          item.phone,
        ].some((value) => normalizeText(value).includes(search));
      })
      .sort((a, b) => {
        const left = a.graduationDate || new Date(0);
        const right = b.graduationDate || new Date(0);
        return right - left;
      });
  }, [courseLookup, searchTerm, signups]);

  const insightCards = [
    { label: 'Learners completed', value: rows.length, note: 'Students who reached the finish line.', tone: 'graduated' },
    { label: 'Learning paths', value: courses.length, note: 'Courses represented in your alumni record.', tone: 'blue' },
    { label: 'Keep the connection', value: signups.length, note: 'The wider learner history in this workspace.', tone: 'green' },
  ];

  if (loading) {
    return <LoadingState label="Loading graduated students..." />;
  }

  return (
    <StudentStatusPageShell
      title="Graduated Students"
      description="Celebrate completed journeys, keep alumni records close, and understand the outcomes your learning business creates."
      kicker="Students"
      stats={[]}
      insightCards={insightCards}
      className="graduated-students-page"
      tableTitle="Graduated Students"
      tableMeta={`${rows.length} records`}
      tableDescription="Students who have completed their studies."
      searchId="graduated-students-search"
      searchValue={searchTerm}
      onSearchChange={(event) => setSearchTerm(event.target.value)}
      searchPlaceholder="Search student, course, note..."
      rows={rows}
      columns={[
        {
          key: 'student',
          label: 'Student',
          render: (item) => (
            <div className="appsStudentCell">
              <div className="application-avatar" aria-hidden="true">
                {String(formatStudentName(item)).trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'ST'}
              </div>
              <div className="application-applicant-copy">
                <strong>{formatStudentName(item)}</strong>
                <span>{item.intakeLabel || 'Open intake'}</span>
              </div>
            </div>
          ),
        },
        { key: 'courseName', label: 'Course', render: (item) => item.courseName || '-' },
        { key: 'intakeLabel', label: 'Cohort', render: (item) => item.intakeLabel || 'Open intake' },
        { key: 'graduationDate', label: 'Graduated On', render: (item) => (item.graduationDate ? formatDate(item.graduationDate) : '-') },
        { key: 'graduationNote', label: 'Note', render: (item) => item.graduationNote || '-' },
        {
          key: 'view',
          label: 'View student',
          render: (item) => (
            <Button to={`/view-student?applicationId=${encodeURIComponent(item.id)}`} variant="secondary" className="appsViewBtn">
              View
            </Button>
          ),
        },
      ]}
      empty={(
        <EmptyState
          title="No graduated students yet."
          message="Students marked as graduated will appear here."
          icon={ClipboardList}
        />
      )}
    />
  );
}
